import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, Cell
} from 'recharts';
import { AlertTriangle, CheckCircle, Download, TrendingUp, FileText, Loader2 } from 'lucide-react';
import { assessments } from '../services/api';

const SocialSkillsDashboard = ({ assessmentData }) => {
  const dashboardData = assessmentData?.parsedData || null;
  const loading = false;
  const error = !dashboardData ? 'No assessment processed yet' : null;
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [parentReport, setParentReport] = useState(assessmentData?.latestAssessment?.parentReport || null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState(assessmentData?.latestAssessment?.parentReportGeneratedAt || null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;
    
    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const patient = assessmentData?.latestAssessment?.patient;
      const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Patient';
      const date = new Date().toISOString().split('T')[0];
      const filename = `Social_Skills_Dashboard_${patientName.replace(/\s+/g, '_')}_${date}.pdf`;

      // Fix width so PDF looks the same regardless of screen/window size
      const el = dashboardRef.current;
      const originalWidth = el.style.width;
      el.style.width = '1024px';

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: el.scrollHeight,
        width: 1024
      });

      el.style.width = originalWidth;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const margin = 10;
      const ratio = (pdfWidth - 2 * margin) / imgWidth;
      const scaledWidth = pdfWidth - 2 * margin;
      const scaledHeight = imgHeight * ratio;

      const x = margin;
      const y = 10;

      if (scaledHeight > pdfHeight - 20) {
        let position = 0;
        const pageHeight = pdfHeight - 20;
        
        while (position < scaledHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          
          const remainingHeight = scaledHeight - position;
          const currentPageHeight = Math.min(pageHeight, remainingHeight);
          
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          
          tempCanvas.width = imgWidth;
          tempCanvas.height = (currentPageHeight / ratio);
          
          tempCtx.drawImage(canvas, 
            0, position / ratio, imgWidth, currentPageHeight / ratio,
            0, 0, imgWidth, currentPageHeight / ratio
          );
          
          const tempImgData = tempCanvas.toDataURL('image/png');
          pdf.addImage(tempImgData, 'PNG', x, y, scaledWidth, currentPageHeight);
          
          position += currentPageHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }
      
      pdf.setFontSize(16);
      pdf.setTextColor(40, 40, 40);
      pdf.text('Social Skills Assessment Dashboard', pdfWidth / 2, 8, { align: 'center' });

      // Add version footer to all pages
      const { getVersionString } = await import('../config/version');
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(getVersionString(), pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save(filename);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  // Generate parent report
  const generateParentReport = async () => {
    const assessmentId = assessmentData?.latestAssessment?.id;
    if (!assessmentId) return;

    setIsGeneratingReport(true);
    setReportError(null);
    try {
      const response = await assessments.generateParentReport(assessmentId);
      if (response.data.status === 'success') {
        setParentReport(response.data.data.report);
        setReportGeneratedAt(new Date().toISOString());
      } else {
        setReportError(response.data.message || 'Erro ao gerar relatório');
      }
    } catch (err) {
      console.error('Error generating parent report:', err);
      setReportError(err.response?.data?.message || 'Erro ao gerar relatório. Tente novamente.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Simple markdown renderer for the parent report
  const renderMarkdown = (markdown) => {
    if (!markdown) return null;
    const lines = markdown.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-gray-800 mt-6 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        const items = [];
        while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
          items.push(
            <li key={i} className="text-gray-700 mb-1">
              {renderInlineMarkdown(lines[i].replace(/^[-*]\s/, ''))}
            </li>
          );
          i++;
          while (i < lines.length && lines[i].trim() === '') i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className="ml-4 list-disc mb-2">
            {items}
          </ul>
        );
        continue;
      } else if (/^\d+\.\s/.test(line)) {
        // Collect consecutive numbered items (with description paragraphs) into an <ol>
        const items = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
          const titleLine = lines[i].replace(/^\d+\.\s/, '');
          i++;
          while (i < lines.length && lines[i].trim() === '') i++;
          const descParts = [];
          while (i < lines.length && !/^\d+\.\s/.test(lines[i]) && !lines[i].startsWith('#') && !lines[i].startsWith('---')) {
            if (lines[i].trim() === '') { i++; continue; }
            descParts.push(
              <p key={`desc-${i}`} className="text-gray-700 mt-1 leading-relaxed">
                {renderInlineMarkdown(lines[i])}
              </p>
            );
            i++;
          }
          items.push(
            <li key={`li-${items.length}`} className="text-gray-700 mb-3">
              {renderInlineMarkdown(titleLine)}
              {descParts}
            </li>
          );
        }
        elements.push(
          <ol key={`ol-${i}`} className="ml-4 list-decimal mb-2">
            {items}
          </ol>
        );
        continue;
      } else if (line.startsWith('---')) {
        elements.push(<hr key={i} className="my-4 border-gray-200" />);
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(
          <p key={i} className="text-gray-700 mb-2 leading-relaxed">
            {renderInlineMarkdown(line)}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  const renderInlineMarkdown = (text) => {
    // Handle **bold** patterns
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error || !dashboardData?.SOCIAL_SKILLS) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <div className="text-yellow-800 font-medium mb-2">Social Skills Assessment Dashboard</div>
        <div className="text-yellow-700 text-sm">
          {error || 'The assessment needs to be analyzed before the dashboard can be displayed.'}
        </div>
      </div>
    );
  }

  const socialSkillsData = dashboardData.SOCIAL_SKILLS;
  const overallStats = socialSkillsData.overall_stats;
  const processedData = socialSkillsData.processed_data;
  const patientInfo = {
    name: assessmentData?.latestAssessment?.patient 
      ? `${assessmentData.latestAssessment.patient.firstName} ${assessmentData.latestAssessment.patient.lastName}`
      : 'Paciente',
    age: 7, // TODO: Get from patient data
    assessmentDate: new Date().toLocaleDateString('pt-BR')
  };

  // Helper to prepare module data for a given level
  const getModuleData = (level) => {
    const levelData = processedData?.[level];
    if (!levelData) return [];
    return Object.keys(levelData).map(module => {
      const moduleInfo = levelData[module];
      return {
        name: formatModuleName(module),
        shortName: module,
        percentage: moduleInfo.percentage,
        mastered: moduleInfo.mastered,
        total: moduleInfo.total,
        emerging: moduleInfo.emerging,
        notMastered: moduleInfo.notMastered
      };
    }).sort((a, b) => b.percentage - a.percentage);
  };

  // Helper to calculate average percentage for a level
  const getLevelPercentage = (level) => {
    const levelData = processedData?.[level];
    if (!levelData) return 0;
    const modules = Object.values(levelData);
    return Math.round(modules.reduce((sum, m) => sum + m.percentage, 0) / modules.length);
  };

  const moduleDataLevel1 = getModuleData('level1');
  const moduleDataLevel2 = getModuleData('level2');
  const moduleDataLevel3 = getModuleData('level3');

  const level1Percentage = getLevelPercentage('level1');
  const level2Percentage = getLevelPercentage('level2');
  const level3Percentage = getLevelPercentage('level3');

  // Helper functions
  function formatModuleName(moduleName) {
    const nameMap = {
      "Atenção Compartilhada": "Atenção\nCompartilhada",
      "Cumprimentos": "Cumprimentos", 
      "Brincadeira Social": "Brincadeira\nSocial",
      "Auto Consciência": "Auto\nConsciência",
      "Conversas": "Conversas",
      "Tomada de Perspectiva": "Tomada de\nPerspectiva",
      "Pensamento Crítico": "Pensamento\nCrítico",
      "Linguagem Avançada": "Linguagem\nAvançada",
      "Desenvolvendo Amizades": "Amizades",
      "Vida na Comunidade": "Vida\nComunitária"
    };
    return nameMap[moduleName] || moduleName;
  }


  function getBarColor(percentage) {
    if (percentage >= 80) return '#10B981';  // Green for good mastery
    if (percentage >= 60) return '#F59E0B';  // Yellow for moderate mastery
    return '#EF4444';                        // Red for low mastery
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-800">{data.shortName}</p>
          <p className="text-brand-600">Porcentagem: {data.percentage}%</p>
          <p className="text-green-600">Dominadas: {data.mastered}/{data.total}</p>
          <p className="text-yellow-600">Emergindo: {data.emerging}</p>
        </div>
      );
    }
    return null;
  };


  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={exportToPDF}
          disabled={isExporting}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
            isExporting 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
          }`}
        >
          {isExporting ? (
            <>
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Exportando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </>
          )}
        </button>
      </div>

      {/* Dashboard Content */}
      <div ref={dashboardRef} className="bg-white">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">🤝 Dashboard Social Skills</h2>
              <p className="text-sm text-gray-600 mt-1">
                {patientInfo.name} • {patientInfo.age} anos • Avaliação: {patientInfo.assessmentDate}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-brand-500">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-brand-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Habilidades Totais</div>
                  <div className="text-2xl font-bold text-gray-900">{overallStats.total_skills}</div>
                  <div className="text-xs text-gray-500">Total estrutural</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Dominadas</div>
                  <div className="text-2xl font-bold text-gray-900">{overallStats.mastered_skills}</div>
                  <div className="text-xs text-gray-500">Habilidades adquiridas</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-yellow-500">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-yellow-500 text-white flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">E</span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Emergindo</div>
                  <div className="text-2xl font-bold text-gray-900">{overallStats.emerging_skills}</div>
                  <div className="text-xs text-gray-500">Em desenvolvimento</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-purple-500 text-white flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">%</span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Taxa de Progresso</div>
                  <div className="flex space-x-3 mt-1">
                    <div>
                      <div className="text-lg font-bold text-brand-600">{level1Percentage}%</div>
                      <div className="text-xs text-gray-400">N1</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-brand-600">{level2Percentage}%</div>
                      <div className="text-xs text-gray-400">N2</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-purple-600">{level3Percentage}%</div>
                      <div className="text-xs text-gray-400">N3</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How to read this report */}
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 text-sm text-gray-700">
            <p className="font-medium text-gray-800 mb-2">Como ler este relatório</p>
            <p className="mb-1">
              As habilidades são organizadas em <strong>3 níveis</strong>: <strong>N1</strong> (básico — cumprimentar, brincar junto), <strong>N2</strong> (intermediário — conversar, fazer amizades) e <strong>N3</strong> (avançado — resolver problemas, linguagem complexa).
            </p>
            <p className="mb-1">
              Cada habilidade é avaliada em <strong>3 ambientes</strong>: individual (1:1 com o terapeuta), em grupo e no ambiente natural (casa, escola, comunidade).
              Uma habilidade só é considerada <strong>"Dominada"</strong> quando a criança demonstra em todos os 3 ambientes.
            </p>
            <p className="mb-1">
              <strong>Dominada</strong> = a criança já realiza com consistência &nbsp;•&nbsp; <strong>Emergindo</strong> = já demonstra em alguns ambientes, mas ainda não em todos &nbsp;•&nbsp; <strong>Não dominada</strong> = ainda não demonstra
            </p>
            <p>
              Nos gráficos: <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#10B981'}}></span> <strong>Verde</strong> (≥80%) = bom domínio &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#F59E0B'}}></span> <strong>Amarelo</strong> (60–79%) = domínio moderado &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#EF4444'}}></span> <strong>Vermelho</strong> (&lt;60%) = precisa de atenção
            </p>
          </div>

          {/* Level Charts */}
          {[
            { label: 'Nível 1', subtitle: 'Habilidades Básicas — cumprimentar, olhar quando chamado, brincar junto, seguir regras simples', data: moduleDataLevel1 },
            { label: 'Nível 2', subtitle: 'Habilidades Intermediárias — manter conversas, entender o ponto de vista do outro, fazer amizades', data: moduleDataLevel2 },
            { label: 'Nível 3', subtitle: 'Habilidades Avançadas — resolver problemas sociais, linguagem complexa, participar da comunidade', data: moduleDataLevel3 }
          ].map(({ label, subtitle, data }) => data.length > 0 && (
            <div key={label} className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-1">Progresso por Módulo - {label}</h3>
              <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      fontSize={12}
                      interval={0}
                    />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="percentage" radius={[4, 4, 0, 0]}>
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.percentage)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}


          {/* Clinical Recommendations */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recomendações Clínicas</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Áreas Prioritárias</h4>
                <div className="space-y-2">
                  {[...moduleDataLevel1, ...moduleDataLevel2, ...moduleDataLevel3]
                    .filter(module => module.percentage < 60)
                    .sort((a, b) => a.percentage - b.percentage)
                    .reduce((unique, m) => unique.find(u => u.shortName === m.shortName) ? unique : [...unique, m], [])
                    .slice(0, 3)
                    .map((module, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span className="text-sm font-medium">{module.shortName}</span>
                      <span className="text-sm text-red-600">{module.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-3">Próximos Passos</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Intervenção intensiva nas áreas prioritárias</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Reavaliação em 6 meses</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-brand-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Meta: N1 ≥ {Math.max(85, level1Percentage + 10)}% | N2 ≥ {Math.max(70, level2Percentage + 10)}% | N3 ≥ {Math.max(50, level3Percentage + 10)}% em 12 meses</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Parent Report Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Relatório para Família</h3>
                <p className="text-sm text-gray-500">
                  Relatório em linguagem acessível para compartilhar com os pais
                  {reportGeneratedAt && (
                    <span className="ml-2 text-gray-400">
                      — Gerado em {new Date(reportGeneratedAt).toLocaleDateString('pt-BR')} às {new Date(reportGeneratedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={generateParentReport}
                disabled={isGeneratingReport}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isGeneratingReport
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
                }`}
              >
                {isGeneratingReport ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {parentReport ? 'Gerar Novamente' : 'Gerar Relatório'}
                  </>
                )}
              </button>
            </div>

            {reportError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{reportError}</p>
              </div>
            )}

            {parentReport && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 prose prose-sm max-w-none">
                {renderMarkdown(parentReport)}
              </div>
            )}

            {!parentReport && !reportError && !isGeneratingReport && (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Clique em "Gerar Relatório" para criar um relatório em linguagem acessível para os pais.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialSkillsDashboard;